import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { getData, postData } from "../../../FetchApiServices";
import { useToast, useConfirm, usePermissions } from "../Layout/T2TUIProvider";

// BUG-16: Detail-modal helpers — small read-only display components for the
// vendor submission preview. Kept inline so this stays a single-file change.
const DetailSection = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
      color: "var(--t2t-text-muted)", textTransform: "uppercase",
      paddingBottom: 6, borderBottom: "1px solid var(--t2t-border)",
      marginBottom: 8,
    }}>{title}</div>
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 6, columnGap: 12, fontSize: 12 }}>
      {children}
    </div>
  </div>
);
DetailSection.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

const DetailRow = ({ label, value, valueNode, mono = false, multiline = false }) => {
  const hasValue = valueNode || (value !== null && value !== undefined && String(value).trim() !== "");
  return (
    <>
      <div style={{ color: "var(--t2t-text-muted)", fontWeight: 600 }}>{label}</div>
      <div style={{
        color: hasValue ? "var(--t2t-text)" : "var(--t2t-text-muted)",
        fontStyle: hasValue ? "normal" : "italic",
        fontFamily: mono && hasValue ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
        whiteSpace: multiline ? "pre-wrap" : "normal",
        wordBreak: "break-word",
      }}>
        {hasValue ? (valueNode || value) : "(none)"}
      </div>
    </>
  );
};
DetailRow.propTypes = {
  label:     PropTypes.string.isRequired,
  value:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueNode: PropTypes.node,
  mono:      PropTypes.bool,
  multiline: PropTypes.bool,
};

// ARCH-03 Shape B: admin queue for vendor self-registration requests.
// Visible to users with `vendor_portal.manage` permission.

const T2TVendorSignupsTab = () => {
  const toast   = useToast();
  const confirm = useConfirm();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("vendor_portal.manage");

  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [actingOn, setActingOn] = useState(null); // invitation_id currently being approved/rejected
  const [viewing,  setViewing]  = useState(null); // invitation object currently in detail modal (BUG-16)

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    const resp = await getData("t2t/vendor-portal/pending-self-invites");
    if (resp.error) setError(resp.error.message || "Failed to load self-invites.");
    else setList(Array.isArray(resp.data?.data) ? resp.data.data : []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const approve = async (inv) => {
    const ok = await confirm(
      `Approve "${inv.vendor_name}" (${inv.contact_email})? The vendor will be emailed a portal link.`,
      "Approve self-invite",
    );
    if (!ok) return;
    setActingOn(inv.invitation_id);
    const resp = await postData(`t2t/vendor-portal/approve/${inv.invitation_id}`, {});
    if (resp.error) toast(resp.error.message || "Approval failed", "error");
    else { toast("Approved — portal link sent.", "success"); setViewing(null); fetchList(); }
    setActingOn(null);
  };

  const reject = async (inv) => {
    const ok = await confirm(
      `Reject "${inv.vendor_name}"? This cannot be undone.`,
      "Reject self-invite",
    );
    if (!ok) return;
    setActingOn(inv.invitation_id);
    const resp = await postData(`t2t/vendor-portal/reject/${inv.invitation_id}`, {});
    if (resp.error) toast(resp.error.message || "Rejection failed", "error");
    else { toast("Rejected.", "success"); setViewing(null); fetchList(); }
    setActingOn(null);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  // BUG-13: turnover bracket value → human label (mirrors public form bracket list)
  const fmtTurnover = (v) => {
    const map = {
      lt_1cr:     "< ₹1 Crore",
      "1cr_10cr": "₹1 – 10 Crore",
      "10cr_50cr":"₹10 – 50 Crore",
      "50cr_250cr":"₹50 – 250 Crore",
      gt_250cr:   "> ₹250 Crore",
    };
    return map[v] || v;
  };

  // BUG-12: Public vendor sign-up link, derived from current origin so it works in dev/test/prod.
  const signupUrl = (typeof window !== "undefined" ? window.location.origin : "") + "/vendor-signup";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(signupUrl);
      toast("Link copied — paste it into a chat or email.", "success");
    } catch {
      toast("Couldn't copy automatically. Please select and copy the link manually.", "error");
    }
  };

  const emailVendor = () => {
    const subject = "You're invited to register as a vendor";
    const body =
      `Hello,\n\n` +
      `You've been invited to register as a vendor on our portal.\n` +
      `Please click the link below to fill in your company details:\n\n` +
      `${signupUrl}\n\n` +
      `Once you submit the form, our team will review your details and email you a secure portal link to continue.\n\n` +
      `Thank you.`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const previewPage = () => {
    window.open(signupUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {/* BUG-12: Share sign-up link panel — gives non-tech admins a one-click way to invite vendors. */}
      {canManage && (
        <div style={{
          border: "1px solid var(--t2t-border)",
          borderRadius: 8,
          padding: 14,
          marginBottom: 14,
          background: "var(--t2t-primary-light, #F0FDF4)",
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Share Vendor Sign-up Link</div>
          <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 10 }}>
            Send this link to vendors so they can self-register. Once they submit, they&apos;ll appear in the list below for approval.
          </div>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 10px",
            background: "#fff",
            border: "1px solid var(--t2t-border)",
            borderRadius: 6,
            marginBottom: 10,
            fontSize: 12,
            wordBreak: "break-all",
          }}>
            <span style={{ flex: 1, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "var(--t2t-text-secondary)" }}>
              {signupUrl}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={copyLink}>
              📋 Copy link
            </button>
            <button type="button" className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={emailVendor}>
              ✉ Email vendor
            </button>
            <button type="button" className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={previewPage}>
              ↗ Preview page
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>
          Pending sign-ups submitted via the public <code>/vendor-signup</code> page.
          Approving emails the vendor a secure portal link.
        </div>
        <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={fetchList} disabled={loading}>
          {loading ? "Loading..." : "↻ Refresh"}
        </button>
      </div>

      {error && <div style={{ padding: 12, color: "var(--t2t-danger)", fontSize: 13, background: "#FEF2F2", borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {!canManage && !error && (
        <div style={{ padding: 12, color: "#92400E", fontSize: 12, background: "#FEF3C7", borderRadius: 6, marginBottom: 12 }}>
          You are viewing this list in read-only mode. Approval requires the <code>vendor_portal.manage</code> permission.
        </div>
      )}

      {/* BUG-16: Detail modal */}
      {viewing && (
        <div className="t2t-overlay" onClick={() => setViewing(null)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">{viewing.vendor_name}</div>
                <div className="t2t-modal-sub">Vendor sign-up submission · {fmtDate(viewing.created_at)}</div>
              </div>
              <button className="t2t-close-btn" onClick={() => setViewing(null)}>×</button>
            </div>
            <div className="t2t-modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <DetailSection title="Company">
                <DetailRow label="Vendor name"     value={viewing.vendor_name} />
                <DetailRow label="Vendor type"     value={viewing.account_group} />
                <DetailRow label="Annual turnover" value={fmtTurnover(viewing.annual_turnover_bracket)} />
                <DetailRow label="Website" valueNode={
                  viewing.website
                    ? <a href={viewing.website.startsWith("http") ? viewing.website : `https://${viewing.website}`}
                         target="_blank" rel="noopener noreferrer"
                         style={{ color: "var(--t2t-primary)" }}>{viewing.website}</a>
                    : null
                } />
                <DetailRow label="GST number"     value={viewing.gst_number} mono />
                <DetailRow label="Services"       value={viewing.business_description} multiline />
              </DetailSection>

              <DetailSection title="Primary Contact">
                <DetailRow label="Name"   value={viewing.contact_name} />
                <DetailRow label="Email"  valueNode={
                  viewing.contact_email
                    ? <a href={`mailto:${viewing.contact_email}`} style={{ color: "var(--t2t-primary)" }}>{viewing.contact_email}</a>
                    : null
                } />
                <DetailRow label="Mobile" valueNode={
                  viewing.contact_mobile
                    ? <a href={`tel:${viewing.contact_mobile}`} style={{ color: "var(--t2t-primary)" }}>{viewing.contact_mobile}</a>
                    : null
                } />
              </DetailSection>

              <DetailSection title="Registered Address">
                <DetailRow label="Address line 1" value={viewing.address_line1} />
                <DetailRow label="City"           value={viewing.city} />
                <DetailRow label="State"          value={viewing.state} />
                <DetailRow label="Country"        value={viewing.country} />
                <DetailRow label="Pincode"        value={viewing.pincode} mono />
              </DetailSection>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <>
                  <button
                    className="t2t-btn t2t-btn-ghost"
                    style={{ color: "var(--t2t-danger)" }}
                    disabled={actingOn === viewing.invitation_id}
                    onClick={() => reject(viewing)}
                  >
                    Reject
                  </button>
                  <button
                    className="t2t-btn t2t-btn-primary"
                    disabled={actingOn === viewing.invitation_id}
                    onClick={() => approve(viewing)}
                  >
                    {actingOn === viewing.invitation_id ? "Approving..." : "Approve vendor"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="t2t-admin-table">
          {list.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "var(--t2t-text-muted)", fontSize: 13 }}>
              No pending sign-ups.
            </div>
          ) : (
            list.map((inv) => (
              <div className="t2t-admin-row" key={inv.invitation_id} style={{ alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{inv.vendor_name}</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-secondary)", marginTop: 2 }}>
                    {inv.contact_name} · {inv.contact_email}
                    {inv.contact_mobile && <> · {inv.contact_mobile}</>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 2 }}>
                    Type: <strong>{inv.account_group}</strong> · Submitted {fmtDate(inv.created_at)}
                  </div>
                  {/* BUG-13: business + address summary */}
                  {(inv.address_line1 || inv.gst_number || inv.business_description) && (
                    <div style={{
                      marginTop: 8,
                      padding: "8px 10px",
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: 6,
                      fontSize: 11,
                      color: "var(--t2t-text-secondary)",
                      display: "grid",
                      gap: 4,
                    }}>
                      {inv.address_line1 && (
                        <div><strong>Address:</strong> {inv.address_line1}{inv.city && `, ${inv.city}`}{inv.state && `, ${inv.state}`}{inv.pincode && ` – ${inv.pincode}`}{inv.country && `, ${inv.country}`}</div>
                      )}
                      {inv.website && (
                        <div><strong>Website:</strong> <a href={inv.website.startsWith("http") ? inv.website : `https://${inv.website}`} target="_blank" rel="noopener noreferrer" style={{ color: "var(--t2t-primary)" }}>{inv.website}</a></div>
                      )}
                      {inv.gst_number && (
                        <div><strong>GST:</strong> <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{inv.gst_number}</span></div>
                      )}
                      {inv.annual_turnover_bracket && (
                        <div><strong>Turnover:</strong> {fmtTurnover(inv.annual_turnover_bracket)}</div>
                      )}
                      {inv.business_description && (
                        <div style={{ marginTop: 2 }}><strong>Services:</strong> {inv.business_description}</div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="t2t-btn t2t-btn-outline t2t-btn-sm"
                    onClick={() => setViewing(inv)}
                    title="View full submission details"
                  >
                    👁 View details
                  </button>
                  {canManage && (
                    <>
                      <button
                        className="t2t-btn t2t-btn-primary t2t-btn-sm"
                        disabled={actingOn === inv.invitation_id}
                        onClick={() => approve(inv)}
                      >
                        {actingOn === inv.invitation_id ? "..." : "Approve"}
                      </button>
                      <button
                        className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                        style={{ color: "var(--t2t-danger)" }}
                        disabled={actingOn === inv.invitation_id}
                        onClick={() => reject(inv)}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
};

export default T2TVendorSignupsTab;
